import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../../common/constants";
import { IResponse, IServerError } from "../../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	sortDDLByLabel,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
} from "../../../../common/Common";

import { AuthService } from "../../../../services";
import { EmployeeReportDataGridWithColumnToggled } from "./EmployeeReportDataGridWithColumnToggled";
import { EmployeeService } from "../../../../services/EmployeeService";
import { EmployeeSetupReportHeader } from "../../Employee/EmployeeSetupReport/EmployeeSetupReportHeader";
import FileSaver from "file-saver";
import { IAppState } from "../../../../store/reducers";
import { IDropDownListResponse } from "../../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../../models";
import { LookupFieldService } from "../../../../services/LookupFieldService";
import { ReportsService } from "../../../../services/ReportsService";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IEmployeeReportSetupPage { }
const EMPLOYEE_REPORT_QUERY_KEY = "employee-report";

const useEmployeesReport = (
	runGetListAPICall: boolean,
	clientId: string,

	payFrequency: string | number,
	employeeStatus: string | number,
	payMethod: string | number,
	provinceOfEmployment: string,
	params: IGetListRequest, // must be serializable/stable,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			EMPLOYEE_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			payFrequency,
			employeeStatus,
			payMethod,
			provinceOfEmployment,
			params, // make sure params is stable (memoized or primitive values)
		],
		queryFn: () =>
			ReportsService.getEmployeeReport(
				runGetListAPICall,
				clientId,

				payFrequency,
				employeeStatus,
				payMethod,
				provinceOfEmployment,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});
export const useGetLookupFieldData = (lookupFieldId: string) =>
	useQuery({
		queryKey: [GET_QUERY_KEY.LOOKUP_FIELD_DATA_BY_ID, lookupFieldId],
		queryFn: (data) => {
			return LookupFieldService.getByIdd(lookupFieldId);
		},
		refetchOnWindowFocus: false,
	});

const useGetMaskStatus = () =>
	useQuery({
		queryKey: ["mask-status"],
		queryFn: (data) => {
			return AuthService.getMaskStatus();
		},
		refetchOnWindowFocus: false,
	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeBasedProvincesOfEmploymentDDL = (clientId: string,) =>
	useQuery({
		queryKey: ["province-of-employment-ddl"],
		queryFn: (data) => {
			return ReportsService.getEmployeeBasedProvincesOfEmploymentDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});



export const EmployeeReportSetup: React.FC<IEmployeeReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [employeeSetupReportData, setEmployeeSetupReportData] =
		useState<any>(undefined);

	const {
		isLoading: isLoadingPayFrequency,
		data: employeeReportSetupPayFrequencyList,
	} = useGetLookupFieldData("payPeriods");

	const { isPending: isPendingMaskStatus, data: maskStatus } =
		useGetMaskStatus();

	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onImportFileClicked = useCallback(() => {
		alert(`Import Icon Clicked!`);
	}, []);

	const onExcelToPdfClicked = useCallback(() => {
		alert(`Excel Icon Clicked!`);
	}, []);

	const onApplyButtonClickedHandler = useCallback(() => {
		const values = formMethods.getValues();

	}, []);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
		useGetPayFrequencyDDL(clientIdStore, taxYear);

	const { isPending: isPendingEmployeeBasedProvinceOfEmployementDDL, data: employeeBasedProvinceOfEmployementDDL } =
		useGetEmployeeBasedProvincesOfEmploymentDDL(clientIdStore);


	const payFrequency = formMethods.getValues("payFrequency");
	const status = formMethods.getValues("status");
	const payMethod = formMethods.getValues("payMethod");
	const ProvinceOfEmployment = formMethods.getValues("ProvinceOfEmployment");

	const { data } = useEmployeesReport(
		runGetListAPICall,
		clientIdStore,

		payFrequency && payFrequency,
		status && status,
		payMethod && payMethod,
		ProvinceOfEmployment && ProvinceOfEmployment,
		queryParams,
		navigateToLogout
	);
	const employeeList: any | null = data;

	useEffect(() => {
		if (employeeList) {
			setEmployeeSetupReportData(employeeList);
		}
	}, [employeeList]);

	//   after click apply button api calls with updated values and update employee report data
	const employeeSetupReportDataAPICall = (
		payFrequency: string | number,
		employeeStatus: string | number,
		payMethod: string | number,
		provinceOfEmployment: string
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			// setQueryParams({
			// 	search_query: "",
			// 	order_by: "firstName",
			// 	order_direction: "asc",
			// 	page: 1,
			// 	size: 25,
			// });
			try {
				const response: any = await ReportsService.getEmployeeReport(
					true,
					clientIdStore,

					payFrequency,
					employeeStatus,
					payMethod,
					provinceOfEmployment,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setEmployeeSetupReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		// let payload = {
		//   status: values.status,
		//   payFrequency: values.payFrequency,
		//   payMethod: values.payMethod,
		//   ProvinceOfEmployment: values.ProvinceOfEmployment,
		// };

		// queryClient.invalidateQueries({
		//   queryKey: [EMPLOYEE_REPORT_QUERY_KEY],
		// });

		employeeSetupReportDataAPICall(
			values.payFrequency,
			values.status,
			values.payMethod,
			values.ProvinceOfEmployment
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.submitEmployeeReportAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				return queryClient.invalidateQueries({
					queryKey: [EMPLOYEE_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});

	useEffect(() => {


		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	// const { isPending: isSubmittingUpdatedRows, mutate: submitUpdatedRows } =
	// 	useMutation({
	// 		mutationFn: EmployeeService.submitUpdatedRows,
	// 		onSuccess: (res: any | IServerError | IResponse) => {

	// 			if (res.status === 200) {
	// 				return queryClient.invalidateQueries({
	// 					queryKey: [EMPLOYEE_REPORT_QUERY_KEY],
	// 				});
	// 			} else {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: res.message
	// 						? res.message
	// 						: "Something went wrong, try again later!",
	// 					severity: "error",
	// 				});
	// 			}
	// 		},
	// 		onError: (err: any) => {
	// 			setSnackbarData({
	// 				open: true,
	// 				message: err.message,
	// 				severity: "error",
	// 			});
	// 		},
	// 	});

	// const { isPending: isSubmittingColumnReorder, mutate: submitColumnReorder } =
	// 	useMutation({
	// 		mutationFn: EmployeeService.submitColumnReorder,
	// 		onSuccess: (res: any | IServerError | IResponse) => {

	// 			if (res.status === 201) {
	// 				return queryClient.invalidateQueries({
	// 					queryKey: [EMPLOYEE_REPORT_QUERY_KEY],
	// 				});
	// 			} else {
	// 				setSnackbarData({
	// 					open: true,
	// 					message: res.message
	// 						? res.message
	// 						: "Something went wrong, try again later!",
	// 					severity: "error",
	// 				});
	// 			}
	// 		},
	// 		onError: (err: any) => {
	// 			setSnackbarData({
	// 				open: true,
	// 				message: err.message,
	// 				severity: "error",
	// 			});
	// 		},
	// 	});

	// useEffect(() => {
	// 	if (onColumnReorder) {
	// 		submitColumnReorder({
	// 			headerColumns: onColumnReorder,
	// 			navigateToLogout: navigateToLogout,
	// 		});
	// 	}
	// }, [onColumnReorder]);

	const employeeReportSetupDDlItems = useMemo(() => {
		if (payFrequencyDDLItems) {
			if (
				payFrequencyDDLItems &&
				Array.isArray(payFrequencyDDLItems)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payFrequencyDDLItems).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payFrequencyDDLItems]);

	const employeeBasedProvinceOfEmployementDDLItems = useMemo(() => {
		if (employeeBasedProvinceOfEmployementDDL) {
			if (
				employeeBasedProvinceOfEmployementDDL &&
				Array.isArray(employeeBasedProvinceOfEmployementDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeBasedProvinceOfEmployementDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeBasedProvinceOfEmployementDDL]);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const upadtedEmployeeListItems = useMemo(() => {
		return (
			employeeSetupReportData &&
			employeeSetupReportData.items &&
			employeeSetupReportData?.items.map((item: any) => ({
				...item,

				provinceOfEmployment:
					item.provinceOfEmployment && item.provinceOfEmployment.id
						? item.provinceOfEmployment.id
						: item.provinceOfEmployment,
				country:
					item.country && item.country.id ? item.country.id : item.country,
				province:
					item.province && item.province.id ? item.province.id : item.province,
				states: item.states && item.states.id ? item.states.id : item.states,
				city: item.city && item.city.id ? item.city.id : item.city,
				dateOfBirth: item.dateOfBirth.charAt(2) === "*" ? "Unmask to view" : item.dateOfBirth,
				socialInsuranceNumber: item.socialInsuranceNumber && item.socialInsuranceNumber?.charAt(2) === "*" ? "Unmask to view" : item.socialInsuranceNumber,
				email: item.email?.charAt(2) === "*" ? "Unmask to view" : item.email,
			}))
		);
	}, [employeeSetupReportData]);

	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;

	const values = watch([
		"payFrequency",
		"status",
		"payMethod",
		"ProvinceOfEmployment",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [payFrequency, status, payMethod, provinceOfEmployment] = values;

		// if (maskStatus) {
		// 	setSnackbarData({
		// 		open: true,
		// 		message: "Please unmask your data.",
		// 		severity: "error",
		// 	});
		// } else {
		try {
			setIsLoading(true);
			const response = await ReportsService.exportEmployeeReport(
				selectedOption,
				clientIdStore,
				payFrequency,
				status,
				payMethod,
				provinceOfEmployment,
				queryParams,
				navigateToLogout
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",
							// message: "Please Unmask sensitive data for PDF printing.",
							severity: "error",
						});
					} else {
						const filename = `${clientName} - Employee-Report.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",
							// message: "Please Unmask sensitive data for PDF printing.",
							severity: "error",
						});
					} else {
						const filename = `${clientName} - Employee-Report.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName} - Employee-Report.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
		// }
	};

	const expandedLabels = ["Reports", "Employees"];
	const expandedEmployeeListOrSetupItem = "Setup"; // Special case for "List" under "Employee"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Employees" &&
				item.label === expandedEmployeeListOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedEmployeeListOrSetupItem && "/reports/employee/setup",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Employee Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<EmployeeSetupReportHeader
						employeeReportSetupDDlItems={employeeReportSetupDDlItems}
						employeeBasedProvinceOfEmployementDDL={employeeBasedProvinceOfEmployementDDLItems}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						// isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isPendingPayFrequencyDDL ||
						isPendingEmployeeBasedProvinceOfEmployementDDL ||
						// isSubmittingUpdatedRows ||
						isLoading
					}
					testId="employee-report"
				/>

				<EmployeeReportDataGridWithColumnToggled
					columns={
						employeeSetupReportData && employeeSetupReportData.headerColumns
							? employeeSetupReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
					}
					completeHeaderColumnsList={
						employeeSetupReportData && employeeSetupReportData.totalHeaderColumns &&
							Object.keys(employeeSetupReportData.totalHeaderColumns).length !== 0
							? employeeSetupReportData.totalHeaderColumns
							: {}
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						employeeSetupReportData && upadtedEmployeeListItems
							? upadtedEmployeeListItems
							: []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					onImportButtonClickedHandler={onImportFileClicked}
					onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						employeeSetupReportData && employeeSetupReportData.total
							? employeeSetupReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Employee Report"}
					onExportButtonClick={onExportButtonClick}
				/>

				{employeeSetupReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
